import { deadlineFromGoal } from '@goaltree/backend/convex/lib/goalDeadline'
import {
  DEFAULT_PLAN_ANSWERS,
  PLAN_DAY_PARTS,
  PLAN_LEVELS,
  type PlanAnswers,
  type PlanDayPart,
  type PlanDeadlineAnswer,
  deadlineAnswer,
  deadlineDate,
  deadlineOptions,
} from '@goaltree/backend/convex/lib/onboardingFlow'
import { TIME_BUDGET_CHOICES } from '@goaltree/backend/convex/lib/timeBudget'
import { FREE_GOAL_LIMIT } from '@goaltree/backend/convex/lib/usage'
import { Slider } from '@goaltree/ui/components/slider'
import { cn } from '@goaltree/ui/lib/utils'
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { useConvex, useConvexAuth } from 'convex/react'
import { Pencil } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import GoalBuildLoader from '@/components/goal-build-loader'
import { GoalAnalyzing, GoalDetailsCard, GoalSplitCard } from '@/components/goal-split-card'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { GoalFlowSteps, OnboardingHeader } from '@/components/onboarding-progress'
import { SiteHeader } from '@/components/site-header'
import { useGoals } from '@/hooks/use-goals'
import { useIsPro } from '@/hooks/use-is-pro'
import { useOnboarding } from '@/hooks/use-onboarding'
import { useOnboardingMode } from '@/hooks/use-onboarding-mode'
import { analyzeGoal, planGoal } from '@/lib/plan-goal'
import { chosenSplit, chosenSplitTitles, useOnboardingStore } from '@/store/onboarding.store'

export const Route = createFileRoute('/goals/new/questions')({
  beforeLoad: () => {
    if (!useOnboardingStore.getState().goalText) {
      throw redirect({ to: '/goals/new', search: { goal: undefined } })
    }
  },
  component: QuestionsComponent,
})

// Monday first; 0 = Sun … 6 = Sat.
const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 0]

// Experience and time budget are picks, the deadline a slider; all start on
// defaults, so the plan builds from "Create my plan".
function QuestionsComponent() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const store = useOnboardingStore()
  const answers = useOnboardingStore((s) => s.planAnswers)
  const { decompose, error, streamKey, reconnecting } = useOnboarding()
  const { tier, isPro } = useIsPro()
  const onboarding = useOnboardingMode()
  const { isAuthenticated } = useConvexAuth()
  const convex = useConvex()
  const goals = useGoals()
  const [building, setBuilding] = useState<string | null>(null)
  const startedRef = useRef(false)
  const analysis = useOnboardingStore((s) => s.analysis)
  const splitChoice = useOnboardingStore((s) => s.splitChoice)
  const analyzed = analysis?.for === store.goalText
  const room = isPro
    ? Infinity
    : Math.max(
        0,
        FREE_GOAL_LIMIT -
          (isAuthenticated
            ? (goals ?? []).filter((g) => !g.isInbox && g.status !== 'done').length
            : 0),
      )
  const splitTitles = splitChoice === 'split' ? chosenSplitTitles() : []

  async function analyze() {
    const text = store.goalText
    const res = await analyzeGoal(convex, text, i18n.language)
    if (useOnboardingStore.getState().goalText !== text) return null
    if (useOnboardingStore.getState().analysis?.for !== text)
      store.setAnalysis({ ...res, for: text })
    return res
  }

  useEffect(() => {
    if (!analyzed) void analyze()
  }, [store.goalText])

  async function build(final: PlanAnswers) {
    if (startedRef.current) return
    if (final.startMode === 'date' && !final.startDate) return
    startedRef.current = true
    // Clicked before the check came back: show what it found instead of planning.
    if (!analyzed) {
      const res = await analyze()
      if (res && (res.goals.length || res.questions.length)) {
        startedRef.current = false
        return
      }
    }
    const { analysis: found, splitChoice: choice } = useOnboardingStore.getState()
    const chosen = choice === 'split' ? chosenSplit() : []
    const titles = chosen.map((c) => c.title)
    const split = titles.length >= 2
    setBuilding(split ? titles[0] : store.goalText)
    store.setSplitRun(
      split
        ? {
            titles,
            parts: chosen.map((c) => c.part),
            queue: titles.slice(1),
            index: 1,
            total: titles.length,
            source: store.goalText,
          }
        : null,
    )
    const tree = split
      ? await planGoal(decompose, titles[0], final, i18n.language, {
          fullText: store.goalText,
          otherGoals: titles.slice(1),
          part: chosen[0].part,
        })
      : await planGoal(decompose, store.goalText, final, i18n.language, {
          areas: found && found.goals.length > 1 ? found.goals : undefined,
        })
    if (!tree) {
      startedRef.current = false
      setBuilding(null)
      return
    }
    store.setGeneratedTree(tree)
    await navigate({ to: '/goals/new/preview' })
  }

  const goalDue = useMemo(
    () => deadlineFromGoal(store.goalText, Date.now(), i18n.language),
    [store.goalText, i18n.language],
  )
  const deadlines = useMemo(() => deadlineOptions(goalDue), [goalDue])
  const current = {
    ...DEFAULT_PLAN_ANSWERS,
    ...answers,
    deadline: deadlineAnswer(answers, goalDue),
    startMode: answers.startMode ?? 'now',
  }
  const deadlineIdx = Math.max(0, deadlines.indexOf(current.deadline))
  function update(next: PlanAnswers) {
    store.setPlanAnswers(next)
    store.setGeneratedTree(null)
  }
  function answer(id: 'level' | 'budget' | 'deadline', value: string) {
    if ((id === 'budget' ? (current.budget ?? 'auto') : current[id]) === value) return
    update({ ...answers, [id]: value })
  }
  function answerStartMode(value: 'now' | 'date' | 'unsure') {
    if (current.startMode === value) return
    update({ ...answers, startMode: value, ...(value !== 'date' ? { startDate: undefined } : {}) })
  }
  function answerStartDate(value: string) {
    update({ ...answers, startMode: 'date', startDate: value })
  }
  function toggleDay(d: number) {
    const days = answers.days ?? []
    update({ ...answers, days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d] })
  }
  function toggleDayPart(p: PlanDayPart) {
    update({ ...answers, dayPart: answers.dayPart === p ? undefined : p })
  }
  const fmtDay = (ms: number) =>
    new Date(ms).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
  function deadlineLabel(d: PlanDeadlineAnswer) {
    if (d !== 'goal') return t(`onboarding.flow.deadline.${d}`)
    const due = new Date(goalDue!.dueDate)
    const farOff = goalDue!.dueDate - Date.now() > 330 * 86_400_000
    return t('onboarding.flow.deadline.goal', {
      date:
        goalDue!.kind === 'month'
          ? due.toLocaleDateString(i18n.language, {
              month: 'long',
              ...(farOff && { year: 'numeric' }),
            })
          : due.toLocaleDateString(i18n.language, {
              month: 'short',
              day: 'numeric',
              ...(farOff && { year: 'numeric' }),
            }),
    })
  }
  const dueAt = deadlineDate(current.deadline, Date.now(), goalDue)
  const localToday = (() => {
    const d = new Date()
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
    ].join('-')
  })()
  const startNeedsDate = current.startMode === 'date' && !answers.startDate
  const weekdayFmt = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' })
  // 2026-10-04 was a Sunday.
  const weekdayName = (d: number) => weekdayFmt.format(new Date(2026, 9, 4 + d))

  function goBack() {
    if (router.history.length > 1) router.history.back()
    else navigate({ to: '/goals/new', search: { goal: undefined } })
  }

  const header = onboarding.active ? (
    <OnboardingHeader step={onboarding.step} onBack={building ? undefined : goBack} />
  ) : (
    <>
      <MobileScreenHeader
        title={t('nav.newGoal')}
        showInbox={false}
        showSearch={false}
        onBack={goBack}
      />
      <SiteHeader showBack className="hidden lg:flex" onBack={goBack} />
    </>
  )

  if (building) {
    return (
      <>
        {header}
        <GoalBuildLoader goal={building} streamKey={streamKey} />
        {reconnecting && (
          <p className="pb-6 text-center text-sm text-muted-foreground">
            {t('planner.reconnecting')}
          </p>
        )}
      </>
    )
  }

  return (
    <>
      {header}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden pb-4">
        {/* Ambient blobs */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none">
          <div className="absolute -top-40 left-1/2 h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-primary/[0.05] blur-[120px]" />
          <div className="absolute right-0 bottom-0 h-[300px] w-[300px] rounded-full bg-[#A66BFF]/[0.05] blur-[100px]" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)',
              backgroundSize: '64px 64px',
            }}
          />
        </div>

        <div className="relative mx-auto w-full max-w-xl px-6 py-6">
          {/* The onboarding header carries its own progress. */}
          {!onboarding.active && (
            <GoalFlowSteps
              step={2}
              onStep={() => navigate({ to: '/goals/new', search: { goal: store.goalText } })}
              className="mb-6"
            />
          )}

          <h1 className="mb-2 text-3xl font-black tracking-tight">
            {t('planner.questionsPre')}{' '}
            <span className="bg-gradient-to-r from-[#a8ff3e] to-[#3DA9FC] bg-clip-text text-transparent">
              {t('planner.questionsGradient')}
            </span>
          </h1>
          <p className="mb-6 text-muted-foreground">{t('planner.questionsSubtitle')}</p>

          <div className="space-y-6">
            <button
              type="button"
              onClick={() => navigate({ to: '/goals/new', search: { goal: store.goalText } })}
              className="group flex w-full cursor-pointer items-start gap-3 rounded-2xl border border-border/60 bg-card/40 px-4 py-3 text-left backdrop-blur-sm transition-colors hover:border-border"
            >
              <span className="min-w-0 flex-1">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-muted-foreground/60">
                  {t('planner.yourGoal')}
                </span>
                <span className="line-clamp-4 block font-semibold text-foreground">
                  {store.goalText}
                </span>
              </span>
              <Pencil className="mt-0.5 size-4 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-foreground" />
            </button>

            {!analyzed && <GoalAnalyzing />}
            {analyzed && (
              <>
                <GoalSplitCard room={room} onChange={() => store.setGeneratedTree(null)} />
                <GoalDetailsCard onChange={() => store.setGeneratedTree(null)} />
              </>
            )}

            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t('onboarding.flow.levelQuestion')}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {PLAN_LEVELS.map((opt) => {
                  const selected = current.level === opt
                  return (
                    <button
                      key={opt}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => answer('level', opt)}
                      className={cn(
                        'flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl border px-2 py-2.5 text-center text-sm leading-tight font-medium text-balance transition-colors',
                        selected
                          ? 'border-primary/60 bg-primary/10 text-foreground'
                          : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border/80 hover:text-foreground',
                      )}
                    >
                      {t(`onboarding.flow.level.${opt}`)}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">{t('timeBudget.question')}</p>
              <div className="flex flex-wrap gap-2">
                {TIME_BUDGET_CHOICES.map((opt) => {
                  const selected = (current.budget ?? 'auto') === opt
                  return (
                    <button
                      key={opt}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => answer('budget', opt)}
                      className={cn(
                        'cursor-pointer rounded-full border px-3.5 py-2 text-sm font-medium transition-colors',
                        selected
                          ? 'border-primary/60 bg-primary/10 text-foreground'
                          : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border/80 hover:text-foreground',
                      )}
                    >
                      {t(`timeBudget.choice.${opt}`)}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground">
                  {t('onboarding.flow.deadlineQuestion')}
                </p>
                <span className="flex shrink-0 items-baseline gap-2">
                  {dueAt && current.deadline !== 'goal' && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {fmtDay(dueAt)}
                    </span>
                  )}
                  <span className="rounded-full bg-primary/15 px-3 py-1 text-sm font-bold text-primary-text">
                    {deadlineLabel(current.deadline)}
                  </span>
                </span>
              </div>
              <div className="space-y-2 px-1">
                <Slider
                  value={[deadlineIdx]}
                  onValueChange={(val) => {
                    const i = Array.isArray(val) ? val[0] : val
                    if (deadlines[i]) answer('deadline', deadlines[i])
                  }}
                  min={0}
                  max={deadlines.length - 1}
                  step={1}
                  aria-label={t('onboarding.flow.deadlineQuestion')}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{deadlineLabel(deadlines[0])}</span>
                  <span>{deadlineLabel(deadlines[deadlines.length - 1])}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t('onboarding.flow.startQuestion')}
              </p>
              <div className="flex flex-wrap gap-2">
                {(['now', 'date', 'unsure'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={current.startMode === mode}
                    onClick={() => answerStartMode(mode)}
                    className={cn(
                      'cursor-pointer rounded-full border px-3.5 py-2 text-sm font-medium transition-colors',
                      current.startMode === mode
                        ? 'border-primary/60 bg-primary/10 text-foreground'
                        : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border/80 hover:text-foreground',
                    )}
                  >
                    {t(`onboarding.flow.startMode.${mode}`)}
                  </button>
                ))}
              </div>
              {current.startMode === 'date' && (
                <div className="space-y-1">
                  <input
                    type="date"
                    min={localToday}
                    value={answers.startDate ?? ''}
                    onChange={(event) => answerStartDate(event.target.value)}
                    aria-label={t('onboarding.flow.startQuestion')}
                    className="h-10 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
                  />
                  <p className="text-xs text-muted-foreground">{t('onboarding.flow.startHint')}</p>
                </div>
              )}
              {current.startMode === 'unsure' && (
                <p className="text-xs text-muted-foreground">{t('onboarding.flow.startHint')}</p>
              )}
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t('onboarding.flow.daysQuestion')}{' '}
                <span className="font-normal text-muted-foreground">
                  {t('onboarding.flow.optional')}
                </span>
              </p>
              <div className="grid grid-cols-7 gap-1.5">
                {WEEK_DAYS.map((d) => {
                  const selected = !!answers.days?.includes(d)
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleDay(d)}
                      className={cn(
                        'cursor-pointer rounded-lg border py-2 text-center text-xs font-medium transition-colors',
                        selected
                          ? 'border-primary/60 bg-primary/10 text-foreground'
                          : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border/80 hover:text-foreground',
                      )}
                    >
                      {weekdayName(d)}
                    </button>
                  )
                })}
              </div>
              <div className="flex flex-wrap gap-2">
                {PLAN_DAY_PARTS.map((p) => {
                  const selected = answers.dayPart === p
                  return (
                    <button
                      key={p}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleDayPart(p)}
                      className={cn(
                        'cursor-pointer rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
                        selected
                          ? 'border-primary/60 bg-primary/10 text-foreground'
                          : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border/80 hover:text-foreground',
                      )}
                    >
                      {t(`onboarding.flow.dayPart.${p}`)}
                    </button>
                  )
                })}
              </div>
            </div>

            {tier !== 'pro' && (
              <p className="text-center text-xs text-muted-foreground">
                {t('onboarding.flow.trialNote')}
              </p>
            )}
            {error && <p className="text-center text-sm text-destructive">{error}</p>}
          </div>
        </div>
      </div>

      <div
        className={cn(
          'bg-background/80 px-4 pt-4 backdrop-blur-sm',
          isAuthenticated && !onboarding.active
            ? 'pb-[calc(57px+env(safe-area-inset-bottom,0px)+1rem)] lg:pb-6'
            : 'pb-[max(1.5rem,env(safe-area-inset-bottom))]',
        )}
      >
        <div className="mx-auto max-w-xl">
          <button
            type="button"
            disabled={startNeedsDate}
            onClick={() =>
              store.generatedTree ? navigate({ to: '/goals/new/preview' }) : void build(current)
            }
            className="group flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-all hover:scale-[1.01] hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
          >
            {store.generatedTree
              ? t('onboarding.flow.keepPlan')
              : splitTitles.length >= 2
                ? t('planner.split.create', { n: splitTitles.length })
                : t('planner.createPlan')}
            <svg
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </>
  )
}
